/**
 * Driver Assigned Block Mode — Phase 2
 * ---------------------------------------------------------------------
 * The driver's active workflow once a block is claimed. Replaces the
 * marketplace map (driver can NOT see competing blocks). Layout:
 *
 *   ┌───────────────────────────────┐
 *   │  Block Summary Bar  ($82 · …) │
 *   ├───────────────────────────────┤
 *   │                               │
 *   │   Route Timeline              │
 *   │   ● Pickup Sarah Johnson  ✓   │
 *   │   ● Dropoff Sarah Johnson    │
 *   │   ○ Pickup Marcus Chen       │
 *   │   ...                        │
 *   │                               │
 *   ├───────────────────────────────┤
 *   │  Morphing Route Pill          │
 *   │  [📞] Sarah J. [💬]  ETA 4 m  │
 *   └───────────────────────────────┘
 *
 * The pill auto-expands at each stop transition (pending → arrived) and
 * auto-collapses on Start Ride. After Complete Ride, the next pending
 * stop becomes "current" — no manual route hunting (spec §5).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, MessageSquare, Check, X, ChevronUp, ChevronDown, ChevronLeft, Navigation as NavIcon, MapPin, Users, Loader2, Play } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";
import { surfaceStyle, surfaceClass } from "@/shared/motion/surface";
import { useDragDismiss } from "@/shared/motion/useDragDismiss";

const APPLE_EASE_OUT = [0.32, 0.72, 0, 1];
const DRAWER_DURATION_S = 0.42;

/**
 * Variants — the Current Block surface lives at the BOTTOM of the
 * driver map (where the Top Opportunity card sits when no block is
 * assigned). When tapped it morphs UP into a full-screen drawer.
 *
 *   pill (card)   — anchored above the MAP|ACCOUNT pill, ~112px tall,
 *                   roomy enough for pay + pickup/dropoff one-liner +
 *                   primary action button.
 *   drawer        — full-bleed sheet from `top: 8px` down 88dvh.
 *   navigating    — drawer slides off-screen, leaving only the bottom
 *                   MorphingRoutePill (handled via separate flag).
 *
 * All positioning uses `top` so framer-motion has one numeric axis to
 * animate (mixing `top` + `bottom` doesn't morph smoothly).
 */
const CB_VARIANTS = {
  pill: {
    width: "min(380px, calc(100vw - 24px))",
    height: "112px",
    // Sit ~96px above the bottom edge so it clears the floating
    // MAP|ACCOUNT pill (50px) + safe-area + a comfortable gap.
    top: "calc(100dvh - 112px - max(env(safe-area-inset-bottom), 16px) - 80px)",
    borderTopLeftRadius: "28px",
    borderTopRightRadius: "28px",
    borderBottomLeftRadius: "28px",
    borderBottomRightRadius: "28px",
    transition: {
      // Close: height shrinks first, then width contracts, then it
      // drops back down to the bottom of the screen.
      height:                  { duration: 0.30, ease: APPLE_EASE_OUT },
      width:                   { duration: 0.26, ease: APPLE_EASE_OUT, delay: 0.18 },
      top:                     { duration: 0.34, ease: APPLE_EASE_OUT, delay: 0.10 },
      borderTopLeftRadius:     { duration: 0.18, ease: APPLE_EASE_OUT, delay: 0.22 },
      borderTopRightRadius:    { duration: 0.18, ease: APPLE_EASE_OUT, delay: 0.22 },
      borderBottomLeftRadius:  { duration: 0.18, ease: APPLE_EASE_OUT, delay: 0.22 },
      borderBottomRightRadius: { duration: 0.18, ease: APPLE_EASE_OUT, delay: 0.22 },
    },
  },
  drawer: {
    width: "100vw",
    height: "88dvh",
    top: "calc(env(safe-area-inset-top, 0px) + 8px)",
    borderTopLeftRadius: "32px",
    borderTopRightRadius: "32px",
    borderBottomLeftRadius: "32px",
    borderBottomRightRadius: "32px",
    transition: {
      // Open: width grows first (card stretches edge-to-edge), then
      // the surface lifts up, then height fills the screen.
      width:                   { duration: 0.26, ease: APPLE_EASE_OUT },
      top:                     { duration: 0.32, ease: APPLE_EASE_OUT, delay: 0.08 },
      height:                  { duration: 0.34, ease: APPLE_EASE_OUT, delay: 0.18 },
      borderTopLeftRadius:     { duration: 0.18, ease: APPLE_EASE_OUT },
      borderTopRightRadius:    { duration: 0.18, ease: APPLE_EASE_OUT },
      borderBottomLeftRadius:  { duration: 0.18, ease: APPLE_EASE_OUT },
      borderBottomRightRadius: { duration: 0.18, ease: APPLE_EASE_OUT },
    },
  },
};

/** Returns the first stop the driver still has work on (status !=
 *  completed/skipped). Mirrors `_next_stop()` on the backend. */
function nextStop(block) {
  if (!block) return null;
  const sorted = [...(block.stops || [])].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
  return sorted.find((s) => !["completed", "skipped"].includes(s.status)) || null;
}

function formatMins(mins) {
  if (!mins) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h)      return `${h}h`;
  return `${m}m`;
}

function formatMinsLong(mins) {
  if (!mins) return "—";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h} hr ${m} min`;
  if (h)      return `${h} hr`;
  return `${m} min`;
}

function formatTime12(hhmm) {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  const hour = ((h + 11) % 12) + 1;
  const am = h < 12;
  return `${hour}:${String(m).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}

export default function AssignedBlockMode({ block, onChange, open, onOpenChange, onCompleteBlock, hidden = false }) {
  // The unified Current-Block surface morphs between three states:
  //   pill       — collapsed, top-center, "Current block · {area}"
  //   drawer     — expanded, full timeline + Navigate CTA
  //   navigating — drawer slides up off-screen; only the bottom pill
  //                stays visible so the map is full-bleed.
  // open === false → pill. open === true → drawer (or navigating).
  const [navigating, setNavigating] = useState(false);
  const [expanded, setExpanded] = useState(true);     // bottom MorphingRoutePill expanded?
  const [busy, setBusy] = useState(false);
  const current = useMemo(() => nextStop(block), [block]);
  const progress = block?.progress || { total: 0, completed: 0, remaining: 0 };

  const handleOpen  = useCallback(() => onOpenChange?.(true),  [onOpenChange]);
  const handleClose = useCallback(() => { setNavigating(false); onOpenChange?.(false); }, [onOpenChange]);

  // Reset whenever block changes.
  useEffect(() => { setNavigating(false); }, [block?.id]);
  // Auto-expand the bottom pill the moment the driver enters navigating.
  useEffect(() => { if (navigating) setExpanded(true); }, [navigating]);
  // Ensure pill returns to its compact shape if parent forces close.
  useEffect(() => { if (!open) setNavigating(false); }, [open]);

  const advance = useCallback(async (action) => {
    if (!current || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        `/driver/marketplace/blocks/${block.id}/stops/${current.id}/${action}`,
        {},
      );
      onChange(data);
      // Auto-collapse after Start Ride (active-ride state per spec).
      if (action === "start") setExpanded(false);
      // Auto-expand after Complete (driver needs to see the next stop).
      if (action === "complete") {
        setExpanded(true);
        if (data?.status === "completed") {
          toast.success("Block complete — nice work!");
          onCompleteBlock?.();
        }
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not update stop");
    } finally {
      setBusy(false);
    }
  }, [busy, current, block?.id, onChange, onCompleteBlock]);

  // Status → primary action label
  const action = useMemo(() => {
    if (!current) return null;
    if (current.status === "pending" || current.status === "en_route") {
      return { label: "Arrived", verb: "arrived" };
    }
    if (current.status === "arrived") return { label: "Start Ride", verb: "start" };
    if (current.status === "in_progress") return { label: "Complete Ride", verb: "complete" };
    return null;
  }, [current]);

  // Drag-to-dismiss — bottom card → drawer morph dismisses with a
  // swipe DOWN. Scroll guard skipped (the drawer content lives in
  // multiple inner scroll containers depending on phase; allowing
  // drag anywhere is the simpler, more iOS-native default).
  const drag = useDragDismiss({
    onDismiss: handleClose,
    origin: "bottom",
    open,
    enabled: !navigating,
  });

  return (
    <>
      {/* Backdrop dims the marketplace map underneath while the
          driver is reviewing the block in drawer mode. Hidden when
          collapsed to pill or when in navigating mode. */}
      <AnimatePresence>
        {open && !navigating && (
          <motion.div
            key="abm-backdrop"
            className="fixed inset-0 z-40 bg-black/40"
            style={{ opacity: drag.backdropOpacity }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: APPLE_EASE_OUT }}
            onClick={handleClose}
            data-testid="assigned-block-backdrop"
          />
        )}
      </AnimatePresence>

      {/* UNIFIED MORPH — one motion.div is the pill in collapsed state
          and the drawer in expanded state. Same Apple-eased variants
          as the driver Earnings morph (width grows first, then height). */}
      <motion.div
        className={`fixed left-1/2 z-50 bg-[#0A1128] text-white overflow-hidden flex flex-col ${surfaceClass()}`}
        style={{
          x: "-50%",
          y: drag.value,
          transformOrigin: "top center",
          // When another full-screen drawer (Earnings, Account, Support)
          // is open we hide the Current-block pill so it doesn't sit on
          // top of those surfaces.
          opacity: hidden ? 0 : 1,
          pointerEvents: hidden ? "none" : undefined,
          transition: "opacity 180ms cubic-bezier(0.32, 0.72, 0, 1)",
          ...surfaceStyle("drawer", { theme: "dark" }),
        }}
        variants={CB_VARIANTS}
        initial="pill"
        animate={open ? "drawer" : "pill"}
        // Slide up off-screen when navigating; brings the drawer back
        // when navigating flips false.
        whileInView={undefined}
        custom={navigating}
        onClick={!open && !hidden ? handleOpen : undefined}
        role={open ? "dialog" : "button"}
        aria-label={open ? "Current block" : "Open current block"}
        aria-modal={open || undefined}
        aria-hidden={hidden || undefined}
        tabIndex={open || hidden ? -1 : 0}
        data-testid={open ? "assigned-block-mode" : "driver-map-active-block-pill"}
        data-state={navigating ? "navigating" : (open ? "drawer" : "pill")}
        // Drag-to-dismiss only while open (collapsed card is tappable).
        drag={open && !navigating ? "y" : false}
        dragConstraints={drag.motionProps.dragConstraints}
        dragElastic={drag.motionProps.dragElastic}
        dragMomentum={drag.motionProps.dragMomentum}
        onDragStart={open && !navigating ? drag.motionProps.onDragStart : undefined}
        onDrag={open && !navigating ? drag.motionProps.onDrag : undefined}
        onDragEnd={open && !navigating ? drag.motionProps.onDragEnd : undefined}
      >
        {/* Optional offscreen translate when navigating. */}
        <motion.div
          className="flex-1 flex flex-col min-h-0"
          animate={{ y: navigating ? "-105%" : 0, opacity: navigating ? 0 : 1 }}
          transition={{
            y:       { duration: DRAWER_DURATION_S, ease: APPLE_EASE_OUT },
            opacity: { duration: DRAWER_DURATION_S * 0.6, ease: APPLE_EASE_OUT },
          }}
        >
          {/* COLLAPSED CARD CONTENT — visible when !open. Replaces the
              old Top Opportunity card at the bottom of the screen.
              Shows enough information for the driver to know what work
              is on deck without having to expand the drawer. */}
          {!open && (
            <div className="h-full w-full px-5 py-3.5 flex items-center gap-4 select-none">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">
                  Current block
                  {block.status === "claimed" && (
                    <span className="text-white/55" data-testid="current-block-pill-ready-tag">· Ready</span>
                  )}
                  {block.status === "in_progress" && (
                    <span className="text-emerald-300">· Live</span>
                  )}
                </div>
                <div className="font-serif text-[22px] leading-tight text-white truncate mt-0.5">
                  ${block.pay_dollars.toFixed(2)} · {block.area_label || "—"}
                </div>
                {current ? (
                  <div className="text-[12px] text-white/65 truncate mt-0.5 flex items-center gap-1">
                    <MapPin size={11} strokeWidth={2.2} className="shrink-0" />
                    <span className="truncate">{current.address || "—"}</span>
                  </div>
                ) : (
                  <div className="text-[12px] text-white/65 mt-0.5">
                    {block.stops_count || 0} stops · {block.estimated_minutes || 0} min
                  </div>
                )}
              </div>
              <div
                className="shrink-0 h-12 w-12 rounded-full bg-[#ed6500] inline-flex items-center justify-center"
                aria-hidden
                data-testid="current-block-card-cta"
              >
                {block.status === "claimed" ? (
                  <Play size={16} strokeWidth={2.4} fill="currentColor" className="text-white" />
                ) : (
                  <NavIcon size={16} strokeWidth={2.4} className="text-white" />
                )}
              </div>
            </div>
          )}

          {/* EXPANDED CONTENT — visible when open */}
          {open && (
            <>
              <BlockSummaryBar block={block} onBack={handleClose} />
              {/* READY-TO-START phase — block is `claimed` but the driver
                  hasn't tapped Start Block yet. We show the route
                  preview + a big Start CTA. Once Start fires, the
                  status flips to `in_progress` (via /start) and the
                  parent re-renders with the timeline+Navigate path. */}
              {block.status === "claimed" && (
                <ReadyToStartPanel block={block} onStarted={onChange} onCompleteBlock={onCompleteBlock} />
              )}
              {block.status !== "claimed" && (
              <>
              <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="route-timeline">
                <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-white/55 mb-3">
                  Block progress · {progress.completed} of {progress.total}
                </div>
                <ol className="space-y-2">
                  {(block.stops || []).sort((a, b) => (a.sequence || 0) - (b.sequence || 0)).map((s) => {
                    const isCurrent = current?.id === s.id;
                    const done = s.status === "completed";
                    return (
                      <li
                        key={s.id}
                        className={`flex items-start gap-3 rounded-2xl p-3 border ${
                          isCurrent ? "bg-[#ed6500]/12 border-[#ed6500]/50"
                          : done    ? "bg-white/[0.03] border-white/10 opacity-60"
                          :           "bg-white/[0.04] border-white/[0.08]"
                        }`}
                        data-testid={`timeline-stop-${s.id}`}
                      >
                        <StopDot kind={s.kind} done={done} current={isCurrent} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-white/55">
                              {s.kind === "pickup" ? `Pickup ${s.sequence}` : `Dropoff ${s.sequence}`}
                            </span>
                            {done && <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-emerald-400">Done</span>}
                            {isCurrent && !done && <span className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[#ed6500]">Current</span>}
                          </div>
                          <div className="text-[14px] font-medium truncate" data-testid={`timeline-stop-${s.id}-name`}>{s.rider_name}</div>
                          <div className="text-[11px] text-white/55 truncate">{s.address}</div>
                        </div>
                      </li>
                    );
                  })}
                </ol>
                {current && (
                  <button
                    type="button"
                    onClick={() => setNavigating(true)}
                    className="w-full mt-4 h-14 rounded-2xl bg-white text-[#0A1128] text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2 transition-transform active:scale-[0.98]"
                    data-testid="assigned-block-navigate"
                  >
                    <NavIcon size={16} strokeWidth={2.2} />
                    Navigate
                  </button>
                )}
              </div>
              {!current && (
                <div className="px-4 pb-[max(env(safe-area-inset-bottom),16px)] pt-2">
                  <div className="rounded-3xl bg-emerald-500/10 border border-emerald-500/30 p-5 text-center" data-testid="block-complete-banner">
                    <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-emerald-300 mb-1">Block complete</div>
                    <div className="font-serif text-[22px] leading-tight">Nice driving.</div>
                    <div className="text-[12px] text-white/65 mt-1">${block.pay_dollars.toFixed(2)} · {progress.total} stops cleared</div>
                    <button
                      type="button"
                      onClick={onCompleteBlock}
                      className="mt-4 px-5 h-11 rounded-full bg-white text-[#0A1128] text-[11px] tracking-[0.22em] uppercase font-semibold"
                      data-testid="block-complete-back"
                    >
                      Back to marketplace
                    </button>
                  </div>
                </div>
              )}
              </>
              )}
            </>
          )}
        </motion.div>
      </motion.div>

      {/* BOTTOM PILL — only mounted when expanded enough to be useful
          (open && current && status='in_progress'). For `claimed`
          blocks the Ready-to-Start panel owns the primary action so
          the bottom pill is suppressed. */}
      {open && current && !hidden && block.status === "in_progress" && (
        <div className="fixed inset-x-0 bottom-0 z-[55] pointer-events-none">
          <MorphingRoutePill
            stop={current}
            block={block}
            expanded={expanded}
            onToggle={() => setExpanded((v) => !v)}
            action={action}
            busy={busy}
            onAction={() => action && advance(action.verb)}
            progress={progress}
            showBackToDrawer={navigating}
            onBackToDrawer={() => setNavigating(false)}
          />
        </div>
      )}
    </>
  );
}


/* ============================  bits  ============================ */

function BlockSummaryBar({ block, onBack }) {
  const p = block?.progress || {};
  // Naive remaining time estimate — proportional to (remaining / total) of
  // the block's estimated_minutes. Replace with real GPS-driven ETA later.
  const remainingMin = block.estimated_minutes && p.total
    ? Math.max(0, Math.round((p.remaining / p.total) * block.estimated_minutes))
    : null;
  // Driver-facing duration formatter used by the Current-block summary
  // bar. Returns full words ("36 min", "1 hr 12 min") instead of the
  // compact `36m / 1h 12m` form so the UI reads naturally next to the
  // pay amount. ETA pill in the metrics row still uses the long form.
  const expectedDuration = block?.estimated_minutes ? formatMinsLong(block.estimated_minutes) : null;
  return (
    <div className="px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 border-b border-white/[0.06] bg-[#0A1128]" data-testid="block-summary-bar">
      {/* Back row — always visible so the driver can return to the
          marketplace at any time (no longer locked into this view). */}
      {onBack && (
        <div className="-mt-1 mb-2">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/55 hover:text-white transition-colors"
            data-testid="block-summary-back"
          >
            <ChevronLeft size={14} strokeWidth={2} />
            <span>Marketplace</span>
          </button>
        </div>
      )}
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <div>
          <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#ed6500]">Current block</div>
          <div className="font-serif text-[20px] leading-tight" data-testid="summary-bar-area">{block.area_label || "Block"}</div>
        </div>
        <div className="text-right">
          <div className="font-serif text-[26px] leading-none" data-testid="summary-bar-pay">${block.pay_dollars.toFixed(2)}</div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-white/55 mt-1" data-testid="summary-bar-duration">
            {expectedDuration ? `About ${expectedDuration}` : "Today"}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Pill label="Stops"      value={p.total}     testId="summary-bar-stops-total" />
        <Pill label="Remaining"  value={p.remaining} testId="summary-bar-remaining" />
        <Pill label="ETA"        value={remainingMin ? formatMins(remainingMin) : "—"} testId="summary-bar-eta" />
      </div>
    </div>
  );
}

function Pill({ label, value, testId }) {
  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] py-2" data-testid={testId}>
      <div className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</div>
      <div className="text-[15px] font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function StopDot({ kind, done, current }) {
  const inner = done ? <Check size={11} strokeWidth={2.4} /> : (kind === "pickup" ? <Users size={11} strokeWidth={2} /> : <MapPin size={11} strokeWidth={2} />);
  return (
    <span
      aria-hidden="true"
      className={`mt-0.5 inline-flex items-center justify-center shrink-0 w-6 h-6 rounded-full text-white text-[10px] ${
        done ? "bg-emerald-500" : current ? "bg-[#ed6500]" : "bg-white/15"
      }`}
    >
      {inner}
    </span>
  );
}

function MorphingRoutePill({ stop, block, expanded, onToggle, action, busy, onAction, progress, showBackToDrawer, onBackToDrawer }) {
  const stopIndex = (progress?.completed || 0) + 1;
  const stopTotal = progress?.total || 1;
  return (
    <div className="px-3 pb-[max(env(safe-area-inset-bottom),12px)] pt-2 pointer-events-auto" data-testid="route-pill">
      <div
        className={`mx-auto max-w-md rounded-3xl bg-[#0A1128]/92 overflow-hidden ${surfaceClass()}`}
        style={surfaceStyle("card", { theme: "dark" })}
      >
        {/* "Show details" handle — only in navigating mode. Tapping
            slides the top drawer back into view so the driver can
            see the full timeline / summary again. */}
        {showBackToDrawer && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onBackToDrawer?.(); }}
            className="w-full pt-2.5 pb-1.5 flex flex-col items-center justify-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold text-white/70 hover:text-white transition-colors"
            data-testid="route-pill-show-details"
          >
            <span className="w-10 h-1 rounded-full bg-white/25" />
            <span>Show block details</span>
          </button>
        )}

        {/* COLLAPSED ROW (always visible) */}
        <button
          type="button"
          onClick={onToggle}
          className="w-full px-3 py-3 flex items-center gap-2 text-left"
          data-testid="route-pill-toggle"
        >
          <a
            href={`tel:${(stop.rider_phone || "").replace(/[^+\d]/g, "")}`}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 w-10 h-10 rounded-full bg-[#ed6500] text-white inline-flex items-center justify-center hover:bg-[#ff7b1f] transition-colors"
            aria-label={`Call ${stop.rider_name}`}
            data-testid="route-pill-call"
          >
            <Phone size={16} strokeWidth={2} />
          </a>
          <div className="flex-1 min-w-0 px-1">
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#ed6500] font-semibold flex items-center gap-1.5">
              <span>{stop.kind === "pickup" ? "Pickup" : "Dropoff"}</span>
              <span className="text-white/35">·</span>
              {/* Stop progress chip — '2 / 4' across the block. Always
                  visible so the driver knows where they are. */}
              <span
                className="px-1.5 py-0.5 rounded-full bg-white/10 text-white/85 text-[9.5px] font-semibold tracking-[0.18em]"
                data-testid="route-pill-progress"
              >
                STOP {stopIndex} / {stopTotal}
              </span>
            </div>
            <div className="text-[14px] font-medium truncate" data-testid="route-pill-name">{stop.rider_name}</div>
          </div>
          <a
            href={`sms:${(stop.rider_phone || "").replace(/[^+\d]/g, "")}`}
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 w-10 h-10 rounded-full bg-white/10 text-white inline-flex items-center justify-center hover:bg-white/20 transition-colors"
            aria-label={`Message ${stop.rider_name}`}
            data-testid="route-pill-text"
          >
            <MessageSquare size={16} strokeWidth={2} />
          </a>
          <div className="shrink-0 ml-1 text-white/55">
            {expanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </div>
        </button>

        {/* EXPANDED BODY (rider full detail + primary action) */}
        {expanded && (
          <div className="px-4 pb-4 pt-1 border-t border-white/[0.06]" data-testid="route-pill-expanded">
            <div className="grid grid-cols-2 gap-2 mt-3 mb-3">
              <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-3">
                <div className="text-[10px] tracking-[0.22em] uppercase text-white/55">{stop.kind === "pickup" ? "Pickup at" : "Dropoff at"}</div>
                <div className="text-[13px] mt-1 leading-snug" data-testid="route-pill-address">{stop.address}</div>
              </div>
              <div className="rounded-xl bg-white/[0.04] border border-white/[0.06] p-3 grid grid-cols-2 gap-2">
                <Tiny label="Seats" value={stop.seats} />
                <Tiny label="Type"  value={stop.transport_preference === "private" ? "Private" : "Shared"} />
                {stop.notes && <div className="col-span-2"><Tiny label="Notes" value={stop.notes} /></div>}
              </div>
            </div>

            {/* Primary action — Arrived → Start Ride → Complete Ride */}
            {action && (
              <button
                type="button"
                onClick={onAction}
                disabled={busy}
                className="w-full h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] disabled:opacity-60 text-white text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2 transition-colors"
                data-testid={`route-pill-action-${action.verb}`}
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                {action.label}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Tiny({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.22em] uppercase text-white/55">{label}</div>
      <div className="text-[12px] mt-0.5">{value}</div>
    </div>
  );
}


/**
 * ReadyToStartPanel — shown inside the Current Block drawer while the
 * block is `claimed` (not yet started). One big CTA — Start Block —
 * advances the backend status to `in_progress` and re-renders the
 * drawer into the live timeline + Navigate flow.
 */
function ReadyToStartPanel({ block, onStarted, onCompleteBlock }) {
  const [busy, setBusy] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const pickup = (block.stops || []).find((s) => s.kind === "pickup");
  const dropoff = (block.stops || []).find((s) => s.kind === "dropoff");
  const legs = Number(block.legs_count || block.stops_count || 0);

  const start = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/driver/marketplace/blocks/${block.id}/start`, {});
      toast.success("Block started");
      onStarted?.(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object") ? detail.message : formatApiError(detail);
      toast.error(msg || "Could not start block");
    } finally {
      setBusy(false);
    }
  };

  const release = async () => {
    if (releasing) return;
    if (!window.confirm("Release this block? It will return to the marketplace.")) return;
    setReleasing(true);
    try {
      await api.post(`/driver/marketplace/blocks/${block.id}/release`, {});
      toast.success("Block released");
      onCompleteBlock?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not release block");
    } finally {
      setReleasing(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-5 py-5" data-testid="ready-to-start-panel">
      <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[#ed6500] mb-2">
        Ready to start
      </div>
      <div className="font-serif font-light text-[28px] text-white leading-tight">
        Tap Start when you&apos;re on your way.
      </div>
      <div className="text-[13px] text-white/65 mt-1.5">
        Pickup window opens at <span className="text-white font-medium">{formatTime12(block.pickup_time || block.window_start)}</span>.
        Starting the block tells the rider you&apos;re en route and unlocks the live navigation flow.
      </div>

      {/* Pickup + Dropoff preview */}
      <div className="mt-6 rounded-2xl bg-white/[0.05] border border-white/10 p-4 space-y-3">
        {pickup && (
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 rounded-full bg-[#ed6500]/15 border border-[#ed6500]/40 inline-flex items-center justify-center mt-0.5 shrink-0">
              <MapPin size={14} strokeWidth={2.2} className="text-[#ed6500]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-white/55">Pickup</div>
              <div className="text-[14px] font-medium truncate" data-testid="ready-to-start-pickup-rider">{pickup.rider_name || "Rider"}</div>
              <div className="text-[12px] text-white/60 truncate">{pickup.address || "—"}</div>
            </div>
          </div>
        )}
        {dropoff && (
          <div className="flex items-start gap-3 pt-3 border-t border-white/10">
            <div className="w-8 h-8 rounded-full bg-emerald-500/15 border border-emerald-500/40 inline-flex items-center justify-center mt-0.5 shrink-0">
              <Check size={14} strokeWidth={2.2} className="text-emerald-300" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-white/55">Dropoff</div>
              <div className="text-[14px] font-medium truncate" data-testid="ready-to-start-dropoff-address">{dropoff.address || "—"}</div>
              <div className="text-[12px] text-white/60">{legs} leg{legs === 1 ? "" : "s"} · {block.estimated_minutes || 0} min total</div>
            </div>
          </div>
        )}
      </div>

      {/* CTA — Start Block */}
      <button
        type="button"
        onClick={start}
        disabled={busy}
        className="w-full mt-6 h-14 rounded-2xl bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[12px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
        data-testid="current-block-start"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} strokeWidth={2.2} fill="currentColor" />}
        {busy ? "Starting…" : "Start Block"}
      </button>

      {/* Release — secondary action, allowed only before start */}
      <button
        type="button"
        onClick={release}
        disabled={releasing || busy}
        className="w-full mt-3 h-12 rounded-2xl bg-transparent border border-white/10 text-white/70 hover:text-white text-[11px] tracking-[0.22em] uppercase font-semibold inline-flex items-center justify-center disabled:opacity-50"
        data-testid="current-block-release"
      >
        {releasing ? "Releasing…" : "Release Block"}
      </button>
    </div>
  );
}
